import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  Users,
  DollarSign,
  TrendingUp,
  BarChart3,
  Target,
  Building2,
  Mail,
  ClipboardList,
  ShieldCheck,
  Gift,
  Clock,
  CheckCircle2,
  XCircle,
  RefreshCw,
} from "lucide-react";
import {
  SectionHeader,
  SummaryRow,
} from "../dashboards/DashboardMedico";
import { useAuthStore } from "../../store/auth";
import { api } from "../../api/greenclub";

const DashboardRepresentante = () => {
  const { user, logout } = useAuthStore();
  const [activeTab, setActiveTab] = useState("dashboard");
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    carregarDashboard();
  }, []);
  const carregarDashboard = async () => {
    try {
      setLoading(true);
      const dashboardResponse = await api.getRepresentativeDashboard();
      if (dashboardResponse.success) {
        setDashboardData(dashboardResponse.data || {});
      } else {
        console.error(
          "Erro ao carregar dados do dashboard:",
          dashboardResponse.error
        );
      }
    } catch (error) {
      console.error("Erro na API ao carregar dashboard:", error);
    } finally {
      setLoading(false);
    }
  };

  const [preCadastroForm, setPreCadastroForm] = useState({
    nome: "",
    email: "",
    telefone: "",
    observacoes: "",
  });
  const [preCadastroErrors, setPreCadastroErrors] = useState({});
  const [isSubmittingPreCadastro, setIsSubmittingPreCadastro] = useState(false);
  const [preCadastroFeedback, setPreCadastroFeedback] = useState(null);
  const [reenviandoId, setReenviandoId] = useState(null);

  const {
    stats,
    territorio,
    clinicasAssociadas,
    vendasRecentes,
    saques,
    cuponsDisponiveis,
  } = dashboardData || {};

  // Estados para alocação de cupom
  const [cupomParaAlocar, setCupomParaAlocar] = useState(null);
  const [clinicaSelecionada, setClinicaSelecionada] = useState("");
  const [alocando, setAlocando] = useState(false);
  const [isRequestingPayout, setIsRequestingPayout] = useState(false);
  const [payoutValue, setPayoutValue] = useState("");
  const [payoutObservation, setPayoutObservation] = useState("");
  const [successMessage, setSuccessMessage] = useState(null);
  const [error, setError] = useState(null);

  const statsCards = [
    {
      title: "Clínicas Ativas",
      value: stats?.clinicasAtivas ?? 0,
      icon: Building2,
      color: "blue",
    },
    {
      title: "Comissões Previstas",
      value: `R$ ${(stats?.comissoesPrevistas ?? 0).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}`,
      icon: TrendingUp,
      color: "purple",
    },
    {
      title: "Comissões Confirmadas",
      value: `R$ ${(stats?.comissoesConfirmadas ?? 0).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}`,
      icon: ShieldCheck,
      color: "emerald",
    },
    {
      title: "Vendas Indiretas",
      value: stats?.vendasIndiretas ?? 0,
      icon: DollarSign,
      color: "orange",
    },
  ];

  const handleReenviarConvite = async (solicitacao) => {
    setReenviandoId(solicitacao.id);
    setPreCadastroFeedback(null);
    try {
      const response = await api.resendClinicInvitation(solicitacao.id);
      if (response.success && response.token) {
        const novoLink = `${window.location.origin}/cadastro/clinica/finalizar?token=${response.token}`;
        setPreCadastroFeedback({
          type: "success",
          message: `Link de convite para ${solicitacao.nome} foi atualizado! Copie e envie: ${novoLink}`,
        });
        // Opcional: recarregar os dados para atualizar a data de expiração na UI
        await carregarDashboard();
      } else {
        throw new Error(
          response.error || "Não foi possível reenviar o convite."
        );
      }
    } catch (err) {
      setPreCadastroFeedback({
        type: "error",
        message: err.message || "Ocorreu um erro ao reenviar o convite.",
      });
    } finally {
      setReenviandoId(null);
    }
  };

  const renderStatusBadge = (status) => {
    const statusConfig = {
      pendente: {
        label: "Pendente",
        Icon: Clock,
        className: "bg-gray-100 text-gray-700",
      },
      aguardando_finalizacao: {
        label: "Convite Enviado",
        Icon: Mail,
        className: "bg-amber-100 text-amber-800",
      },
      aprovado: {
        label: "Cadastro Concluído",
        Icon: CheckCircle2,
        className: "bg-emerald-100 text-emerald-800",
      },
      rejeitado: {
        label: "Rejeitado",
        Icon: XCircle,
        className: "bg-red-100 text-red-800",
      },
    };

    const config = statusConfig[status] || statusConfig.pendente;
    const { Icon } = config;

    return (
      <span
        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${config.className}`}
      >
        <Icon className="w-3.5 h-3.5" />
        {config.label}
      </span>
    );
  };

  const handleAlocarCupom = async () => {
    if (!cupomParaAlocar || !clinicaSelecionada) return;

    setAlocando(true);
    setPreCadastroFeedback(null);
    try {
      const response = await api.allocateCouponToClinic(
        cupomParaAlocar._id,
        clinicaSelecionada
      );
      if (response.success) {
        setPreCadastroFeedback({
          type: "success",
          message: "Cupom alocado com sucesso!",
        });
        setCupomParaAlocar(null);
        setClinicaSelecionada("");
        await carregarDashboard(); // Recarrega os dados para atualizar as listas
      } else {
        throw new Error(response.error || "Falha ao alocar cupom.");
      }
    } catch (err) {
      setPreCadastroFeedback({ type: "error", message: err.message });
    } finally {
      setAlocando(false);
    }
  };

  const handleRepPayoutRequest = async () => {
    const valueNumber = Number(payoutValue.replace(",", "."));

    if (!valueNumber || valueNumber <= 0) {
      alert("Informe um valor válido para solicitar o resgate.");
      return;
    }

    if (valueNumber > (stats?.comissaoDisponivel ?? 0)) {
      alert(
        `O valor solicitado (R$ ${valueNumber.toFixed(2)}) é maior que a sua comissão disponível (R$ ${(stats?.comissaoDisponivel ?? 0).toFixed(2)}).`
      );
      return;
    }

    try {
      setIsRequestingPayout(true);
      const response = await api.requestRepresentativePayout({
        valor: valueNumber,
        observacao: payoutObservation || undefined,
      });

      setSuccessMessage(
        response.message || "Solicitação de resgate registrada com sucesso!"
      );
      setPayoutValue("");
      setPayoutObservation("");

      // Atualiza o estado local para refletir a mudança imediatamente
      setDashboardData((prevData) => {
        const novosSaques = [
          {
            id: `temp-${Date.now()}-${Math.random()}`,
            valor: valueNumber,
            observacao: payoutObservation,
            status: "pendente",
            criado_em: new Date().toISOString(),
          },
          ...(prevData.saques || []),
        ];

        const newStats = {
          ...prevData.stats,
          comissaoDisponivel: prevData.stats.comissaoDisponivel - valueNumber,
          comissaoEmProcessamento:
            (prevData.stats.comissaoEmProcessamento || 0) + valueNumber,
        };

        return { ...prevData, saques: novosSaques, stats: newStats };
      });
    } catch (err) {
      setError(err.message || "Não foi possível registrar a solicitação.");
    } finally {
      setIsRequestingPayout(false);
    }
  };

  // Otimização: Criar um mapa para buscar informações da clínica por ID rapidamente.
  const clinicasMap = React.useMemo(() => {
    if (!clinicasAssociadas) return new Map();
    return new Map(clinicasAssociadas.map((c) => [c.clinica_user_id, c]));
  }, [clinicasAssociadas]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-indigo-50 flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-emerald-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-indigo-50">
      {/* Modal de Alocação de Cupom */}
      {cupomParaAlocar && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl shadow-2xl w-full max-w-md"
          >
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">
                Alocar Cupom
              </h3>
              <p className="text-sm text-gray-600 mt-1">
                Selecione a clínica que receberá o cupom{" "}
                <span className="font-bold text-purple-600">
                  {cupomParaAlocar.codigo}
                </span>
                .
              </p>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label
                  htmlFor="clinica-select"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Clínicas Associadas
                </label>
                <select
                  id="clinica-select"
                  value={clinicaSelecionada}
                  onChange={(e) => setClinicaSelecionada(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                >
                  <option value="" disabled>
                    Selecione uma clínica...
                  </option>
                  {clinicasAssociadas
                    ?.filter((c) => c.statusCadastro === "aprovado")
                    .map((clinica) => (
                      <option
                        key={clinica.clinica_user_id}
                        value={clinica.clinica_user_id}
                      >
                        {clinica.nome}
                      </option>
                    ))}
                </select>
              </div>
              {preCadastroFeedback && preCadastroFeedback.type === "error" && (
                <div className="text-xs font-medium px-3 py-2 rounded-lg text-center bg-red-50 text-red-700 border border-red-200">
                  {preCadastroFeedback.message}
                </div>
              )}
            </div>
            <div className="p-4 bg-gray-50 rounded-b-xl flex justify-end items-center gap-3">
              <button
                onClick={() => {
                  setCupomParaAlocar(null);
                  setPreCadastroFeedback(null);
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-100"
              >
                Cancelar
              </button>
              <button
                onClick={handleAlocarCupom}
                disabled={!clinicaSelecionada || alocando}
                className="px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700 disabled:bg-purple-300 disabled:cursor-not-allowed"
              >
                {alocando ? "Alocando..." : "Confirmar Alocação"}
              </button>
            </div>
          </motion.div>
        </div>
      )}
      {/* Header */}
      <motion.header
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        className="bg-white shadow-sm border-b border-gray-200"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center flex-shrink-0">
              <h1 className="text-2xl font-bold text-emerald-800">GreenClub</h1>
              <span className="hidden md:inline-block ml-3 bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-sm font-medium">
                Representante
              </span>
            </div>
            <div className="flex items-center space-x-4">
              <div className="hidden sm:block bg-purple-100 text-purple-800 px-3 py-1 rounded-lg text-sm font-medium">
                Território: {territorio || "Não definido"}
              </div>
              <span className="text-gray-700">{user?.nome}</span>
              <button
                onClick={logout}
                className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg transition-colors"
              >
                Sair
              </button>
            </div>
          </div>
        </div>
      </motion.header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Tabs */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.1 }}
          className="relative mb-8"
        >
          <div className="overflow-x-auto pb-2 -mb-2">
            <div className="flex space-x-1 bg-white p-1 rounded-lg shadow-sm w-max">
              {[
                { id: "dashboard", label: "Dashboard", icon: BarChart3 },
                { id: "clinicas", label: "Clínicas", icon: Building2 },
                {
                  id: "pre-cadastro",
                  label: "Pré-cadastro de clínicas",
                  icon: Users,
                },
                { id: "cupons", label: "Cupons", icon: TrendingUp },
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center space-x-2 px-3 sm:px-4 py-2 rounded-md transition-colors text-sm whitespace-nowrap ${
                    activeTab === tab.id
                      ? "bg-purple-100 text-purple-700 font-semibold"
                      : "text-gray-600 hover:bg-gray-100 hover:text-gray-800"
                  }`}
                >
                  <tab.icon className="w-4 h-4" />
                  <span>{tab.label}</span>
                </button>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Dashboard Tab */}
        {activeTab === "dashboard" && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="space-y-8"
          >
            {successMessage && (
              <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 px-4 py-3 rounded-lg flex items-center gap-2">
                <ShieldCheck className="w-5 h-5" />
                <span>{successMessage}</span>
                <button
                  onClick={() => setSuccessMessage(null)}
                  className="ml-auto text-emerald-700 text-sm underline"
                >
                  Fechar
                </button>
              </div>
            )}
            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {statsCards.map((stat, index) => (
                <motion.div
                  key={stat.title}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 * index }}
                  className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-center">
                    <div className={`p-3 rounded-lg bg-${stat.color}-100`}>
                      <stat.icon className={`w-6 h-6 text-${stat.color}-600`} />
                    </div>
                    <div className="ml-4">
                      <p className="text-sm text-gray-600">{stat.title}</p>
                      <p className="text-2xl font-bold text-gray-900">
                        {stat.value}
                      </p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Resumo Rápido */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Destaque Clínicas */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Clínicas com melhor performance no mês
                </h3>

                <div className="space-y-3">
                  {clinicasAssociadas?.slice(0, 3).map((clinica, index) => (
                    <div
                      key={clinica.id}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                    >
                      <div className="flex items-center space-x-3">
                        <div
                          className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold ${
                            index === 0
                              ? "bg-yellow-500"
                              : index === 1
                              ? "bg-gray-400"
                              : "bg-orange-400"
                          }`}
                        >
                          {index + 1}
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">
                            {clinica.nome}
                          </p>
                          <p className="text-sm text-gray-600">
                            Cadastrada em:{" "}
                            {new Date(
                              clinica.dataCadastro
                            ).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-medium text-purple-600">
                          {clinica.vendasMes || 0} vendas
                        </p>
                        <p className="text-sm text-gray-600">
                          R$ {clinica.comissaoGerada || "0.00"}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Vendas Recentes */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Vendas Recentes
                </h3>

                <div className="space-y-3">
                  {vendasRecentes?.slice(0, 3).map((venda) => (
                    <div
                      key={venda.id}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                    >
                      <div>
                        <p className="font-medium text-gray-900">
                          {venda.paciente}
                        </p>
                        <p className="text-sm text-gray-600">
                          Clínica: {venda.clinica}
                        </p>
                        <p className="text-xs text-gray-500">{venda.data}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-medium text-gray-900">
                          {venda.valor}
                        </p>
                        <p className="text-sm text-purple-600">
                          +{venda.comissaoRepresentante}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Seção de Comissões e Saques */}
            <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
              <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <SectionHeader
                  icon={BarChart3}
                  title="Resumo de Comissões"
                  description="Acompanhe suas comissões disponíveis e o histórico de resgates."
                />
                <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <SummaryRow
                      icon={DollarSign}
                      label="Comissão Disponível para Resgate"
                      value={`R$ ${(stats?.comissaoDisponivel ?? 0).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}`}
                      caption="Baseado em comissões confirmadas."
                    />
                    <SummaryRow
                      icon={RefreshCw}
                      label="Comissão em Processamento"
                      value={`R$ ${(stats?.comissaoEmProcessamento ?? 0).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}`}
                      caption="Resgates solicitados e em análise."
                    />
                    <SummaryRow
                      icon={CheckCircle2}
                      label="Comissão Paga"
                      value={`R$ ${(stats?.comissaoResgatada ?? 0).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}`}
                      caption="Total de resgates concluídos."
                    />
                  </div>
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <h4 className="text-sm font-semibold text-gray-800 mb-3">
                      Histórico de Resgates
                    </h4>
                    <div className="space-y-3 max-h-48 overflow-y-auto pr-2">
                      {saques?.length > 0 ? (
                        saques.map((payout) => (
                          <div
                            key={payout.id}
                            className="text-sm flex justify-between items-center"
                          >
                            <div>
                              <p className="font-semibold text-gray-800">
                                R${" "}
                                {payout.valor.toLocaleString("pt-BR", {
                                  minimumFractionDigits: 2,
                                })}
                              </p>
                              <p className="text-xs text-gray-500">
                                {new Date(
                                  payout.criado_em
                                ).toLocaleDateString("pt-BR")}
                              </p>
                            </div>
                            <span
                              className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                                getStatusBadgeClass(payout.status).className
                              }`}
                            >
                              {getStatusBadgeClass(payout.status).label}
                            </span>
                          </div>
                        ))
                      ) : (
                        <p className="text-xs text-gray-500 text-center py-4">
                          Nenhum resgate solicitado.
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <SectionHeader
                  icon={Gift}
                  title="Solicitar Resgate"
                  description="Peça o pagamento da sua comissão disponível."
                />
                <div className="mt-4 space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Valor
                    </label>
                    <input
                      value={payoutValue}
                      onChange={(e) => setPayoutValue(e.target.value)}
                      type="number"
                      min="0"
                      step="0.01"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500"
                      placeholder="0,00"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Observações
                    </label>
                    <textarea
                      value={payoutObservation}
                      onChange={(e) => setPayoutObservation(e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 h-20 focus:outline-none focus:ring-2 focus:ring-purple-500"
                      placeholder="Informações para o financeiro"
                    />
                  </div>
                  <button
                    onClick={handleRepPayoutRequest}
                    disabled={isRequestingPayout}
                    className="w-full bg-purple-600 hover:bg-purple-700 text-white font-semibold px-4 py-2.5 rounded-lg flex items-center justify-center gap-2 transition-colors disabled:opacity-60"
                  >
                    {isRequestingPayout ? (
                      "Enviando..."
                    ) : (
                      <>
                        <Mail className="w-4 h-4" />
                        Solicitar
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Clínicas Tab */}
        {activeTab === "clinicas" && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="space-y-6"
          >
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-6">
                Clínicas em acompanhamento
              </h3>

              <div className="space-y-4">
                {clinicasAssociadas?.map((clinica) => (
                  <div
                    key={clinica.id}
                    className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex items-center space-x-4">
                        <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                          <Building2 className="w-6 h-6 text-purple-600" />
                        </div>
                        <div>
                          <h4 className="font-medium text-gray-900">
                            {clinica.nome}
                          </h4>
                          <p className="text-sm text-gray-600">
                            Cadastrada em:{" "}
                            {new Date(
                              clinica.dataCadastro
                            ).toLocaleDateString()}
                          </p>
                          <p className="text-sm text-gray-500">
                            Status:{" "}
                            <span
                              className={`font-medium ${
                                clinica.statusCadastro === "aprovado"
                                  ? "text-emerald-600"
                                  : "text-orange-500"
                              }`}
                            >
                              {clinica.statusCadastro === "aprovado"
                                ? "Ativa"
                                : "Pendente"}
                            </span>
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-bold text-purple-600">
                          R$ {clinica.comissaoGerada || "0.00"}
                        </p>
                        <p className="text-sm text-gray-600">comissão gerada</p>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm bg-gray-50 p-3 rounded-lg">
                      <div className="text-center">
                        <p className="text-gray-600">Vendas Este Mês</p>
                        <p className="font-bold text-gray-900">
                          {clinica.vendasMes || 0}
                        </p>
                      </div>
                      <div className="text-center">
                        <p className="text-gray-600">Frascos Vendidos</p>
                        <p className="font-bold text-purple-600">
                          {clinica.frascosVendidos || 0}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        )}

        {/* Pré-cadastro Tab */}
        {activeTab === "pre-cadastro" && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="space-y-6"
          >
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between mb-6">
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Pré-cadastros enviados
                  </h3>
                  <p className="text-sm text-gray-600">
                    Monitore o status das clínicas convidadas para finalizar o
                    cadastro.
                  </p>
                </div>
                <div className="w-full md:w-1/2 lg:w-1/3">
                  <div className="bg-purple-50 border border-purple-100 rounded-xl p-4">
                    <h4 className="text-sm font-semibold text-purple-800 mb-3 flex items-center gap-2">
                      <Users className="w-4 h-4" />
                      Novo pré-cadastro de clínica
                    </h4>

                    <form
                      className="space-y-3"
                      onSubmit={(event) => {
                        event.preventDefault(); // Corrigido para usar event
                        const errors = {};
                        if (!preCadastroForm.nome.trim())
                          errors.nome = "O nome da clínica é obrigatório";
                        if (!preCadastroForm.email.trim()) {
                          errors.email = "O e-mail é obrigatório";
                        } else if (
                          !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(
                            preCadastroForm.email.trim()
                          )
                        ) {
                          errors.email = "Informe um e-mail válido";
                        }
                        setPreCadastroErrors(errors);
                        if (Object.keys(errors).length > 0) {
                          setPreCadastroFeedback({
                            type: "error",
                            message:
                              "Revise os campos obrigatórios destacados antes de prosseguir.",
                          });
                          return;
                        }
                        setIsSubmittingPreCadastro(true);
                        setPreCadastroFeedback(null);

                        api
                          .createClinicPreRegistrationByRep(preCadastroForm)
                          .then((response) => {
                            if (!response.success)
                              throw new Error(response.error);

                            setPreCadastroForm({
                              nome: "",
                              email: "",
                              telefone: "",
                              observacoes: "",
                            });
                            setPreCadastroErrors({});
                            setPreCadastroFeedback({
                              type: "success",
                              message:
                                "Convite enviado com sucesso! A clínica receberá o link para finalizar o cadastro.",
                            });
                          })
                          .catch((err) => {
                            setPreCadastroFeedback({
                              type: "error",
                              message:
                                err.message ||
                                "Não foi possível enviar o convite.",
                            });
                          })
                          .finally(() => {
                            setIsSubmittingPreCadastro(false);
                          });
                      }}
                    >
                      <div>
                        <label className="block text-xs font-medium text-gray-600 uppercase tracking-wide">
                          Nome da clínica
                        </label>
                        <input
                          type="text"
                          value={preCadastroForm.nome}
                          onChange={(event) =>
                            setPreCadastroForm((prev) => ({
                              ...prev,
                              nome: event.target.value,
                            }))
                          }
                          placeholder="Ex: Clínica Vida Verde"
                          className={`mt-1 w-full px-3 py-2 border ${
                            preCadastroErrors.nome
                              ? "border-red-300 focus:ring-red-400"
                              : "border-purple-200 focus:ring-purple-400"
                          } rounded-lg text-sm focus:outline-none focus:ring-2 focus:border-transparent bg-white`}
                        />
                        {preCadastroErrors.nome && (
                          <p className="mt-1 text-xs text-red-600">
                            {preCadastroErrors.nome}
                          </p>
                        )}
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-gray-600 uppercase tracking-wide">
                          E-mail principal da clínica
                        </label>
                        <input
                          type="email"
                          value={preCadastroForm.email}
                          onChange={(event) =>
                            setPreCadastroForm((prev) => ({
                              ...prev,
                              email: event.target.value,
                            }))
                          }
                          placeholder="contato@clinica.com"
                          className={`mt-1 w-full px-3 py-2 border ${
                            preCadastroErrors.email
                              ? "border-red-300 focus:ring-red-400"
                              : "border-purple-200 focus:ring-purple-400"
                          } rounded-lg text-sm focus:outline-none focus:ring-2 focus:border-transparent bg-white`}
                        />
                        {preCadastroErrors.email && (
                          <p className="mt-1 text-xs text-red-600">
                            {preCadastroErrors.email}
                          </p>
                        )}
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-gray-600 uppercase tracking-wide">
                          Telefone (Opcional)
                        </label>
                        <input
                          type="tel"
                          value={preCadastroForm.telefone}
                          onChange={(event) =>
                            setPreCadastroForm((prev) => ({
                              ...prev,
                              telefone: event.target.value,
                            }))
                          }
                          placeholder="(11) 99999-0000"
                          className={`mt-1 w-full px-3 py-2 border ${
                            preCadastroErrors.telefone
                              ? "border-red-300 focus:ring-red-400"
                              : "border-purple-200 focus:ring-purple-400"
                          } rounded-lg text-sm focus:outline-none focus:ring-2 focus:border-transparent bg-white`}
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-gray-600 uppercase tracking-wide">
                          Observações internas
                        </label>
                        <textarea
                          placeholder="Ex: Clínica com foco em terapias integrativas, agendar visita na próxima semana"
                          value={preCadastroForm.observacoes}
                          onChange={(event) =>
                            setPreCadastroForm((prev) => ({
                              ...prev,
                              observacoes: event.target.value,
                            }))
                          }
                          rows={2}
                          className="mt-1 w-full px-3 py-2 border border-purple-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent bg-white resize-none"
                        />
                      </div>

                      {preCadastroFeedback && (
                        <div
                          className={`text-xs font-medium px-3 py-2 rounded-lg text-center ${
                            preCadastroFeedback.type === "success"
                              ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                              : "bg-red-50 text-red-700 border border-red-200"
                          }`}
                        >
                          {preCadastroFeedback.message}
                        </div>
                      )}

                      <button
                        type="submit"
                        disabled={isSubmittingPreCadastro}
                        className="w-full flex items-center justify-center gap-2 bg-purple-600 hover:bg-purple-700 disabled:bg-purple-300 disabled:cursor-not-allowed text-white py-2 rounded-lg text-sm font-medium transition-colors"
                      >
                        <ClipboardList className="w-4 h-4" />
                        {isSubmittingPreCadastro
                          ? "Enviando convite..."
                          : "Enviar pré-cadastro para a clínica"}
                      </button>

                      <p className="text-[11px] text-purple-600/80 text-center leading-relaxed">
                        Um e-mail com link seguro será enviado automaticamente.
                        A clínica terá 15 dias para completar o cadastro, e você
                        será notificado sobre cada atualização.
                      </p>
                    </form>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {clinicasAssociadas?.map((preCadastro) => (
                  <div
                    key={preCadastro.id || preCadastro.email}
                    className="border border-gray-200 rounded-lg p-4 hover:border-purple-200 hover:shadow-sm transition-colors"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h4 className="text-base font-semibold text-gray-900 flex items-center gap-2">
                          <Building2 className="w-4 h-4 text-purple-600" />
                          {preCadastro.nome}
                        </h4>
                        <p className="text-sm text-gray-600">
                          <Mail className="w-4 h-4 text-gray-400" />
                          {preCadastro.email}
                        </p>
                      </div>
                      {renderStatusBadge(preCadastro.statusCadastro)}
                    </div>

                    <div className="grid grid-cols-2 gap-3 text-sm bg-gray-50 rounded-lg p-3">
                      <div>
                        <p className="text-gray-500">Enviado em</p>
                        <p className="font-medium text-gray-900">
                          {new Date(preCadastro.dataCadastro).toLocaleDateString(
                            "pt-BR"
                          )}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-500">Convite expira em</p>
                        <p className="font-medium text-gray-900">
                          {preCadastro.expiracao ?? "15 dias"}
                        </p>
                      </div>
                      <div className="col-span-2">
                        <p className="text-gray-500">Observações</p>
                        <p className="font-medium text-gray-900">
                          {preCadastro.observacoes || "Sem observações"}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center justify-between mt-4 text-xs text-gray-500">
                      <span>ID: {preCadastro.id}</span>
                      <button
                        onClick={() => handleReenviarConvite(preCadastro)}
                        disabled={reenviandoId === preCadastro.id}
                        className="text-purple-600 hover:text-purple-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {reenviandoId === preCadastro.id
                          ? "Reenviando..."
                          : "Reenviar convite"}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-gradient-to-r from-purple-600 to-indigo-600 rounded-xl text-white p-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
              <div>
                <h4 className="text-lg font-semibold mb-1">
                  Como funciona o fluxo?
                </h4>
                <p className="text-sm text-purple-100 max-w-xl">
                  Cada pré-cadastro gera um link exclusivo com validade de 15
                  dias. Assim que a clínica conclui o preenchimento, você é
                  notificado automaticamente e o status é atualizado nesta
                  lista.
                </p>
              </div>
              <div className="bg-white/10 border border-white/20 px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2">
                <ShieldCheck className="w-4 h-4" />
                Processo seguro e rastreável
              </div>
            </div>
          </motion.div>
        )}

        {/* Cupons Tab */}
        {activeTab === "cupons" && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="space-y-6"
          >
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    Gestão de cupons
                  </h3>
                  <p className="text-sm text-gray-600">
                    Visualize quais cupons podem ser atribuídos e quais já estão
                    sendo usados pelas clínicas.
                  </p>
                </div>
                <div className="flex flex-wrap gap-3">
                  <div className="bg-blue-50 text-blue-700 px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2">
                    <Gift className="w-4 h-4" />
                    {cuponsDisponiveis?.length || 0} cupons disponíveis
                  </div>
                  <div className="bg-emerald-50 text-emerald-700 px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2">
                    <Building2 className="w-4 h-4" />
                    {cuponsDisponiveis?.filter((c) => c.clinica_id).length ||
                      0}{" "}
                    cupons em uso
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                <div className="border border-dashed border-blue-200 rounded-xl p-6 bg-blue-50/40">
                  <h4 className="text-base font-semibold text-blue-900 mb-4 flex items-center gap-2">
                    <Gift className="w-4 h-4" />
                    Cupons disponíveis para alocação
                  </h4>

                  <div className="space-y-4">
                    {cuponsDisponiveis?.length > 0 &&
                      cuponsDisponiveis
                        .filter((c) => !c.clinica_id)
                        .map((cupom) => (
                          <div
                            key={cupom.codigo}
                            className="bg-white rounded-lg p-4 shadow-sm border border-blue-100"
                          >
                            <div className="flex justify-between items-start mb-3">
                              <div>
                                <span className="text-xs uppercase tracking-wide text-blue-500">
                                  Código
                                </span>
                                <p className="text-lg font-semibold text-blue-900">
                                  {cupom.codigo}
                                </p>
                              </div>
                              <div className="px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                                {cupom.tipo === "percentual"
                                  ? `${cupom.valor}%`
                                  : `R$ ${cupom.valor.toFixed(2)}`}
                              </div>
                            </div>

                            <div className="grid grid-cols-2 gap-3 text-sm text-gray-600">
                              <div>
                                <p className="text-gray-500">Criado em</p>
                                <p className="font-medium text-gray-900">
                                  {new Date(cupom.criado_em).toLocaleDateString(
                                    "pt-BR"
                                  )}
                                </p>
                              </div>
                              <div>
                                <p className="text-gray-500">Expira em</p>
                                <p className="font-medium text-gray-900">
                                  {cupom.valido_ate
                                    ? new Date(
                                        cupom.valido_ate
                                      ).toLocaleDateString("pt-BR")
                                    : "Não expira"}
                                </p>
                              </div>
                              <div>
                                <p className="text-gray-500">Público alvo</p>
                                <p className="font-medium text-gray-900">
                                  {cupom.publico || "Geral"}
                                </p>
                              </div>
                              <div className="col-span-2">
                                <p className="text-gray-500">Observação</p>
                                <p className="font-medium text-gray-900">
                                  {cupom.descricao}
                                </p>
                              </div>
                            </div>

                            <div className="mt-4 flex justify-end">
                              <button
                                onClick={() => {
                                  setCupomParaAlocar(cupom);
                                  setPreCadastroFeedback(null);
                                }}
                                className="text-sm font-medium text-blue-600 hover:text-blue-700"
                              >
                                Selecionar clínica
                              </button>
                            </div>
                          </div>
                        ))}
                  </div>
                </div>

                <div className="border border-emerald-200 rounded-xl p-6 bg-emerald-50/40">
                  <h4 className="text-base font-semibold text-emerald-900 mb-4 flex items-center gap-2">
                    <Building2 className="w-4 h-4" />
                    Cupons em uso pelas clínicas
                  </h4>

                  <div className="space-y-4">
                    {cuponsDisponiveis?.length > 0 &&
                      cuponsDisponiveis
                        .filter((c) => c.clinica_id)
                        .map((cupom) => (
                          <div
                            key={cupom.codigo}
                            className="bg-white rounded-lg p-4 shadow-sm border border-emerald-100"
                          >
                            {(() => {
                              const clinica = clinicasMap.get(cupom.clinica_id);
                              return (
                                <div className="flex justify-between items-start mb-3">
                                  <div>
                                    <span className="text-xs uppercase tracking-wide text-emerald-500">
                                      Código
                                    </span>
                                    <p className="text-lg font-semibold text-emerald-900">
                                      {cupom.codigo}
                                    </p>
                                    <p className="text-sm text-gray-600">
                                      Clínica:{" "}
                                      {clinica?.nome || "Não identificada"}
                                    </p>
                                    <p className="text-xs text-gray-500">
                                      Contato: {clinica?.email || "N/A"}
                                    </p>
                                  </div>
                                  <div className="text-right">
                                    <p className="text-xs text-gray-500">
                                      Ativo desde
                                    </p>
                                    <p className="text-sm font-medium text-gray-900">
                                      {new Date(
                                        cupom.criado_em
                                      ).toLocaleDateString("pt-BR")}
                                    </p>
                                    <div className="mt-1 px-3 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">
                                      {cupom.tipo === "percentual"
                                        ? `${cupom.valor}%`
                                        : `R$ ${cupom.valor.toFixed(2)}`}
                                    </div>
                                  </div>
                                </div>
                              );
                            })()}

                            <div className="grid grid-cols-2 gap-3 text-sm text-gray-600">
                              <div>
                                <p className="text-gray-500">Vendas geradas</p>
                                <p className="font-medium text-gray-900">
                                  {cupom.usos_atuais || 0}
                                </p>
                              </div>
                              <div>
                                <p className="text-gray-500">Última venda</p>
                                <p className="font-medium text-gray-900">
                                  {cupom.ultimaVenda || "N/A"}
                                </p>
                              </div>
                              <div>
                                <p className="text-gray-500">
                                  Desconto concedido
                                </p>
                                <p className="font-medium text-gray-900">
                                  R$ {cupom.descontoTotal || "0.00"}
                                </p>
                              </div>
                              <div>
                                <p className="text-gray-500">
                                  Comissão prevista
                                </p>
                                <p className="font-medium text-gray-900">
                                  R$ {cupom.comissaoPrevista || "0.00"}
                                </p>
                              </div>
                            </div>

                            <div className="mt-4 flex justify-end">
                              <button
                                disabled
                                className="text-sm font-medium text-gray-400 cursor-not-allowed"
                              >
                                Gerenciar vínculo
                              </button>
                            </div>
                          </div>
                        ))}
                    {cuponsDisponiveis?.filter((c) => c.clinica_id).length ===
                      0 && (
                      <p className="text-sm text-gray-500 text-center py-4">
                        Nenhum cupom em uso pelas clínicas.
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};
export default DashboardRepresentante;

function getStatusBadgeClass(status) {
  const base = "px-3 py-1 rounded-full text-xs font-semibold";
  switch (status) {
    case "pago":
      return { className: `${base} bg-emerald-100 text-emerald-700`, label: "Pago" };
    case "processando":
      return { className: `${base} bg-blue-100 text-blue-700`, label: "Processando" };
    case "rejeitado":
      return { className: `${base} bg-red-100 text-red-700`, label: "Rejeitado" };
    default:
      return { className: `${base} bg-amber-100 text-amber-700`, label: "Pendente" };
  }
}

function translateStatus(status) {
  const map = {
    pendente: "Pendente",
    processando: "Processando",
    pago: "Pago",
    rejeitado: "Rejeitado",
  };
  return map[status] || status;
}
